import _ from 'lodash'
import { Radio, RadioItem } from '@agro-club/agroclub-shared'
import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { refetchFunc } from 'modules/domain/common/hooks'
import { CurrencyCode, HedgeType } from 'modules/domain/export/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { HrWithHeader } from 'views/components/Hr/Hr'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { FormItemWrapper } from 'views/styled/common'
import { CommonForwardFields } from './CommonForwardFields'
import { CommonOptionFields, HedgingFields } from './CommonOptionFields'
import { CommonNaturalFields } from './CommonNaturalFields'

type Props = {
  exportId: string
  onSuccess: refetchFunc
  onClose: () => void
}

export const AddHedgingModal: FC<Props> = ({ onClose, exportId, onSuccess }) => {
  const { t } = useTranslation(['export', 'common'])
  const { formProgress, formHandler } = useAFormHandler()

  const formik = useFormik<HedgingFields>({
    initialValues: {
      hedge_type: HedgeType.option,
      export_contract: exportId,
      currency: CurrencyCode.USD,
      bank_deal_number: null,
      option_volume: null,
      strike: null,
      expiration_date: null,
      settlement_date: null,
      option_bonus: null,
      receipt_planned_date: null,
    },

    onSubmit: formHandler(
      () => {
        const { bank_deal_number, expiration_date, ...otherValues } = formik.values
        let dto
        switch (formik.values.hedge_type) {
          case HedgeType.option:
            dto = {
              ...otherValues,
              bank_deal_number,
              expiration_date,
            }
            break
          case HedgeType.forward:
            dto = otherValues
            break
          case HedgeType.natural:
            dto = _.pick(formik.values, [
              'hedge_type',
              'export_contract',
              'option_volume',
              'currency',
              'strike',
              'settlement_date',
            ])
            break
        }
        return apiClient.post(endpoints.exportHedges(), dto)
      },
      {
        onSuccess: () => {
          onSuccess()
          onClose()
        },
        onError: (error) => {
          const { errors } = error
          formik.setErrors(errors)
        },
      },
    ),
  })

  let FieldsComponent
  switch (formik.values.hedge_type) {
    case HedgeType.option:
      FieldsComponent = CommonOptionFields
      break
    case HedgeType.forward:
      FieldsComponent = CommonForwardFields
      break
    case HedgeType.natural:
      FieldsComponent = CommonNaturalFields
      break
  }

  return (
    <ResponsiveModal
      id="addHedging"
      title={t('hedging')}
      size="small"
      onClose={onClose}
      onConfirm={formik.submitForm}
      confirmButtonText={t('common:save')}
      progress={formProgress}
    >
      <SharedModalContent>
        <SharedModalInputs>
          <FormItemWrapper>
            <Radio onChange={(type) => formik.setFieldValue('hedge_type', type)} value={formik.values.hedge_type}>
              <RadioItem label={t('hedgeType.option')} value={HedgeType.option} />
              <RadioItem label={t('hedgeType.forward')} value={HedgeType.forward} />
              <RadioItem label={t('hedgeType.natural')} value={HedgeType.natural} />
            </Radio>
          </FormItemWrapper>

          <HrWithHeader text={t('calculationData')} />

          <FieldsComponent formik={formik} />
        </SharedModalInputs>
      </SharedModalContent>
    </ResponsiveModal>
  )
}
